import { useEffect, useState } from 'react';
import slugify from 'slugify';
import {
  Flex,
  Card,
  Heading,
  Container,
  Button,
  Input,
  Icon,
  Textarea,
  Spacer,
  Stack,
} from '@chakra-ui/react';
import { TrashIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { InputGroup } from '@/components/ui/input-group';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { Field } from '@/components/ui/field';
import { Radio, RadioGroup } from '@/components/ui/radio';
import { Checkbox } from '@/components/ui/checkbox';
import FieldOptions from './field-options';
import api from '@/utils/api';
import { BlockType, Field as FieldType, SelectionType, DateType } from '@/lib/types';
import { FIELD_TYPES } from '@/lib/constants';

const formSchema = z.object({
  name: z.string().min(1, 'The name is required'),
  unique_name: z.string().optional(),
  field_type: z.number().min(1, 'The field type is required'),
  options: z.string().optional(),
  properties: z.object({
    selectionType: z.string().optional(),
    minlength: z.number().nullable().optional(),
    maxlength: z.number().nullable().optional(),
    placeholder: z.string().optional(),
    dateType: z.string().optional(),
    showInCalendarView: z.boolean().optional(),
    showAsMetaPill: z.boolean().optional(),
  }),
});

type FormValues = z.infer<typeof formSchema>;

export default function EditField() {
  const field = useLoaderData() as FieldType;
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (field) {
      reset({
        name: field.name,
        unique_name: field.unique_name,
        field_type: field.field_type,
        properties: field.properties,
      });
    }
  }, [field, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!field.id) {
      return;
    }

    try {
      const properties = { ...data.properties };
      switch (data.field_type) {
        case BlockType.Text:
          delete properties.selectionType;
          delete properties.dateType;
          delete properties.showInCalendarView;
          break;
        case BlockType.Date:
          delete properties.selectionType;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          break;
        case BlockType.Selection:
          delete properties.dateType;
          delete properties.showInCalendarView;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          break;
        case BlockType.LongText:
          delete properties.dateType;
          delete properties.showInCalendarView;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          delete properties.showAsMetaPill;
          break;
        default:
          delete properties.selectionType;
          delete properties.dateType;
          delete properties.showInCalendarView;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          delete properties.showAsMetaPill;
          break;
      }

      await api.put(`fields/${field.id}`, {
        name: data.name,
        unique_name: data.unique_name,
        field_type: data.field_type,
        workspace_id: field.workspace_id,
        properties: properties,
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update field',
      });
    }
  };

  async function onDelete() {
    try {
      await api.delete(`fields/${field.id}`);
      setShowDeleteModal(false);
      navigate(`/${field.workspace_id}/fields`);
      toaster.create({ title: 'Field removed', type: 'success' });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete field',
      });
    }
  }

  const reloadData = () => {
    navigate(0);
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Flex mb={4}>
              <Heading size='3xl'>Edit a Field</Heading>
              <Spacer />
              <Button
                size={'sm'}
                colorPalette={'red'}
                onClick={() => {
                  setShowDeleteModal(true);
                }}
              >
                <Icon as={TrashIcon} />
                Delete
              </Button>
            </Flex>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form id={'editField'} onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={4} mb={8}>
                <Field label='Field Type' disabled>
                  <NativeSelectRoot disabled>
                    <NativeSelectField {...register('field_type')}>
                      {FIELD_TYPES.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
                <Field label='Field Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input
                    {...register('name')}
                    onChange={(e) => {
                      const uniqueName = slugify(e.target.value, {
                        lower: true,
                        strict: true,
                      });
                      setValue('unique_name', uniqueName);
                    }}
                  />
                </Field>
                <Field label='Unique Name' disabled>
                  <InputGroup
                    flex='1'
                    w={'full'}
                    endElement={<Icon as={LockClosedIcon} w={4} h={4} />}
                  >
                    <Input {...register('unique_name')} disabled />
                  </InputGroup>
                </Field>
                {field.field_type === BlockType.Text && (
                  <>
                    <Field
                      label='Placeholder'
                      invalid={!!errors.properties?.placeholder}
                      errorText={errors.properties?.placeholder?.message}
                      helperText='Placeholder is optional. It is a short hint that describes the expected value of the field.'
                    >
                      <Input {...register('properties.placeholder')} />
                    </Field>
                    <Field
                      label='Minimum Characters'
                      invalid={!!errors.properties?.minlength}
                      errorText={errors.properties?.minlength?.message}
                      helperText='Minimum Characters is optional. It must be a whole number less than or equal to Maximum Characters (if set).'
                    >
                      <Input
                        {...register('properties.minlength', {
                          setValueAs: (value) => (!value ? null : parseInt(value, 10)),
                        })}
                        type='number'
                        placeholder={'e.g. 10, 140, etc'}
                      />
                    </Field>
                    <Field
                      label='Maximum Characters'
                      invalid={!!errors.properties?.maxlength}
                      errorText={errors.properties?.maxlength?.message}
                      helperText='Maximum Characters is optional. It must be a whole number greater than or equal to Minimum Characters (if set).'
                    >
                      <Input
                        {...register('properties.maxlength', {
                          setValueAs: (value) => (!value ? null : parseInt(value, 10)),
                        })}
                        type='number'
                        placeholder={'e.g. 10, 140, etc'}
                      />
                    </Field>
                  </>
                )}
                {field.field_type === BlockType.Date && (
                  <>
                    <Controller
                      control={control}
                      name='properties.dateType'
                      render={({ field }) => (
                        <Field label='Type'>
                          <RadioGroup
                            name={field.name}
                            value={field.value}
                            onValueChange={({ value }) => {
                              field.onChange(value);
                            }}
                            pl={4}
                          >
                            <Stack gap={2}>
                              <Radio value={DateType.Date}>Date</Radio>
                              <Radio value={DateType.Datetime}>Datetime</Radio>
                            </Stack>
                          </RadioGroup>
                        </Field>
                      )}
                    />

                    {watch('properties.dateType') === DateType.Datetime && (
                      <Controller
                        control={control}
                        name='properties.showInCalendarView'
                        render={({ field }) => (
                          <Field>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={({ checked }) => field.onChange(checked)}
                            >
                              Show in Calendar View
                            </Checkbox>
                          </Field>
                        )}
                      />
                    )}
                  </>
                )}
                {(field.field_type === BlockType.Text ||
                  field.field_type === BlockType.Numeric ||
                  field.field_type === BlockType.Date ||
                  field.field_type === BlockType.Selection) && (
                  <Controller
                    control={control}
                    name='properties.showAsMetaPill'
                    render={({ field }) => (
                      <Field helperText='When enabled, this field appears as a quick-glance pill at the top of the public content page instead of in the body.'>
                        <Checkbox
                          checked={!!field.value}
                          onCheckedChange={({ checked }) => field.onChange(checked)}
                        >
                          Show as meta pill on public page
                        </Checkbox>
                      </Field>
                    )}
                  />
                )}
                {field.field_type === BlockType.Selection && (
                  <>
                    <Controller
                      control={control}
                      name='properties.selectionType'
                      render={({ field }) => (
                        <Field label='Selection Type'>
                          <RadioGroup
                            name={field.name}
                            value={field.value}
                            onValueChange={({ value }) => {
                              field.onChange(value);
                            }}
                            pl={4}
                          >
                            <Stack gap={2}>
                              <Radio value={SelectionType.Single}>Single</Radio>
                              <Radio value={SelectionType.Multi}>Multi</Radio>
                            </Stack>
                          </RadioGroup>
                        </Field>
                      )}
                    />

                    {field.id && field.workspace_id ? (
                      <Field>
                        <FieldOptions
                          workspaceId={field.workspace_id}
                          fieldId={field.id}
                          onSuccess={reloadData}
                        />
                      </Field>
                    ) : (
                      <Field label='Field Options'>
                        <Textarea
                          {...register('options')}
                          placeholder={'First Option,4'}
                          rows={8}
                        />
                      </Field>
                    )}
                  </>
                )}
              </Stack>
              <Button
                colorPalette='brand'
                form={'editField'}
                type={'submit'}
                width={'full'}
                loading={isSubmitting}
                loadingText={'Loading...'}
              >
                Save
              </Button>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>

      <DestructiveModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(!showDeleteModal)}
        onConfirm={() => onDelete()}
        title={'Delete Field'}
        children={`Remove ${field.name} from fields?`}
        confirmText={'Delete'}
      />
    </WorkspaceLayout>
  );
}
